import {
  FETCH_ALL_USERS,
  FETCH_ALL_USERS_SUCCESS,
  FETCH_ALL_USERS_FAILED,
  EDIT_USER,
  EDIT_USER_SUCCESS,
  EDIT_USER_FAILED,
  DELETE_USER,
  DELETE_USER_SUCCESS,
  DELETE_USER_FAILED,
  CLEAR_USERS_ERROR,
  FETCH_ALL_USERS_STATIC,
  FETCH_ALL_USERS_STATIC_SUCCESS,
  FETCH_ALL_USERS_STATIC_FAILED,
  USER_DELETED,
  FETCH_ALL_DRIVERS,
  FETCH_ALL_DRIVERS_SUCCESS,
  FETCH_ALL_DRIVERS_FAILED,
  PATCH_USER_IN_LIST,
  REMOVE_USER_FROM_LIST,
  FETCH_USERS_PAGE,
  FETCH_USERS_PAGE_SUCCESS,
  FETCH_USERS_PAGE_FAILED,
  RESET_USERS_PAGE,
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import { onValue, set, push, update, off, get, remove, ref, query, orderByChild, equalTo, limitToFirst, startAt } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";
import { ADMIN_USERS_PAGE_SIZE, DISPATCH_LOCATION_MAX_AGE_MS } from '../config/economyConfig';

const isDispatchLocationFresh = (location) => {
  if (!location) return false;
  const ts = location.updatedAt || location.at || location.timestamp;
  if (!ts) return true;
  const parsed = typeof ts === 'number' ? ts : Date.parse(ts);
  if (!Number.isFinite(parsed)) return true;
  return Date.now() - parsed <= DISPATCH_LOCATION_MAX_AGE_MS;
};

let usersListenerCache = null;
let locationsListenerCache = null;
let fetchUsersOnceInflight = null;
let driversFetchCache = null;
let locationsFetchCache = null;
let fetchDriversSettings = null;
let driverLocationsPollTimer = null;

const fetchLocationsForDriverIds = async (driverIds) => {
  if (!driverIds || !driverIds.length) return {};
  const { userLocationRef } = firebase;
  const locations = {};
  const ids = driverIds.slice(0, 200);
  await Promise.all(
    ids.map(async (uid) => {
      try {
        const snap = await get(userLocationRef(uid));
        const val = snap.val();
        if (val) locations[uid] = val;
      } catch (e) {
        // Reglas RTDB: solo ubicaciones propias o de conductores aprobados.
      }
    })
  );
  return locations;
};

const refreshDriverLocationsCache = async () => {
  if (!driversFetchCache) return {};
  return fetchLocationsForDriverIds(Object.keys(driversFetchCache));
};

const stopDriverLocationsPoll = () => {
  if (driverLocationsPollTimer) {
    clearInterval(driverLocationsPollTimer);
    driverLocationsPollTimer = null;
  }
};

const USER_TYPES_FOR_LIST = ['customer', 'driver', 'fleetadmin', 'admin'];

const waitForFirebaseAuth = async (maxMs = 15000) => {
  const { auth } = firebase;
  if (!auth) return null;
  if (auth.currentUser) return auth.currentUser;

  const start = Date.now();
  return new Promise((resolve) => {
    let settled = false;
    const finish = (user) => {
      if (settled) return;
      settled = true;
      unsubscribe();
      clearInterval(interval);
      clearTimeout(timeout);
      resolve(user || null);
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) finish(user);
    });

    const interval = setInterval(() => {
      if (auth.currentUser) finish(auth.currentUser);
      else if (Date.now() - start >= maxMs) finish(null);
    }, 250);

    const timeout = setTimeout(() => finish(auth.currentUser || null), maxMs);
  });
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const mergeSnapInto = (merged, snap) => {
  const val = snap && snap.val ? snap.val() : null;
  if (!val) return;
  Object.keys(val).forEach((uid) => {
    if (val[uid]) merged[uid] = val[uid];
  });
};

const fetchUsersByTypeQuery = async (merged, usertype) => {
  const { database } = firebase;
  try {
    const snap = await get(
      query(ref(database, 'users'), orderByChild('usertype'), equalTo(usertype))
    );
    mergeSnapInto(merged, snap);
  } catch (e) {
    console.warn('[fetchUsersOnce] query failed for', usertype, e && e.message ? e.message : e);
  }
};

const fetchAllUsersRecord = async (role) => {
  const { usersRef, driversRef } = firebase;
  const merged = {};

  if (role === 'admin') {
    try {
      mergeSnapInto(merged, await get(usersRef));
    } catch (e) {
      console.warn('[fetchUsersOnce] admin bulk read failed', e && e.message ? e.message : e);
    }
  }

  if (Object.keys(merged).length === 0) {
    for (const usertype of USER_TYPES_FOR_LIST) {
      await fetchUsersByTypeQuery(merged, usertype);
    }
  }

  const hasDrivers = Object.values(merged).some((user) => user && user.usertype === 'driver');
  if (!hasDrivers) {
    try {
      mergeSnapInto(merged, await get(driversRef));
    } catch (e) {
      console.warn('[fetchUsersOnce] driversRef failed', e && e.message ? e.message : e);
    }
  }

  if (Object.keys(merged).length === 0 && role === 'admin') {
    try {
      mergeSnapInto(merged, await get(usersRef));
    } catch (e) {
      console.warn('[fetchUsersOnce] users bulk read retry failed', e && e.message ? e.message : e);
    }
  }

  if (Object.keys(merged).length === 0 && role === 'fleetadmin') {
    try {
      const snap = await get(driversRef);
      const val = snap.val();
      if (val) {
        Object.keys(val).forEach((uid) => {
          const user = val[uid];
          if (user && user.approved) merged[uid] = user;
        });
      }
    } catch (e) {
      console.warn('[fetchUsersOnce] fleetadmin drivers fetch failed', e && e.message ? e.message : e);
    }
  }

  return merged;
};

const mapUsersRecordToArray = (data, locations) =>
  Object.keys(data || {})
    .filter((uid) => data[uid])
    .map((uid) => ({
      ...data[uid],
      id: uid,
      location: locations && locations[uid] ? locations[uid] : null,
    }));

const mergeUsersWithLocations = (users, locations) => {
  if (!users) return [];
  return Object.keys(users)
    .filter((i) => users[i])
    .map((i) => ({
      ...users[i],
      id: i,
      location: locations && locations[i] ? locations[i] : null,
    }));
};

const dispatchMergedUsers = (dispatch) => {
  if (!usersListenerCache) {
    dispatch({
      type: FETCH_ALL_USERS_FAILED,
      payload: "No users available."
    });
    return;
  }
  dispatch({
    type: FETCH_ALL_USERS_SUCCESS,
    payload: mergeUsersWithLocations(usersListenerCache, locationsListenerCache)
  });
};

export const fetchUsers = () => async (dispatch) => {

  const {
    usersRef,
  } = firebase;

  dispatch({
    type: FETCH_ALL_USERS,
    payload: null
  });

  const authUser = await waitForFirebaseAuth();
  if (!authUser) {
    dispatch({
      type: FETCH_ALL_USERS_FAILED,
      payload: "Authentication required."
    });
    return;
  }

  off(usersRef);
  usersListenerCache = null;
  locationsListenerCache = null;

  onValue(usersRef, (snapshot) => {
    usersListenerCache = snapshot.val();
    dispatchMergedUsers(dispatch);
  }, (error) => {
    console.warn('[fetchUsers] users listener failed', error && error.message ? error.message : error);
    dispatch({
      type: FETCH_ALL_USERS_FAILED,
      payload: error && error.message ? error.message : "Failed to fetch users."
    });
  });
};


export const fetchUsersOnce = () => async (dispatch, getState) => {
  const profile = getState && getState().auth && getState().auth.profile;
  if (!profile || !profile.usertype || !['admin', 'fleetadmin'].includes(profile.usertype)) {
    return;
  }

  if (fetchUsersOnceInflight) {
    return fetchUsersOnceInflight;
  }

  const { allLocationsRef } = firebase;

  fetchUsersOnceInflight = (async () => {
    dispatch({
      type: FETCH_ALL_USERS_STATIC,
      payload: null
    });

    try {
      let data = null;
      const maxAttempts = 3;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const authUser = await waitForFirebaseAuth(attempt === 1 ? 8000 : 12000);
        if (!authUser) {
          if (attempt === maxAttempts) {
            dispatch({
              type: FETCH_ALL_USERS_STATIC_FAILED,
              payload: "Authentication required."
            });
            return;
          }
          await delay(1000 * attempt);
          continue;
        }

        data = await fetchAllUsersRecord(profile.usertype);
        if (data && Object.keys(data).length > 0) {
          break;
        }

        if (attempt < maxAttempts) {
          await delay(1000 * attempt);
        }
      }

      if (!data || Object.keys(data).length === 0) {
        dispatch({
          type: FETCH_ALL_USERS_STATIC_FAILED,
          payload: "No users available."
        });
        return;
      }

      let locations = null;
      try {
        if (profile.usertype === 'admin' && data) {
          const driverIds = Object.keys(data).filter(
            (uid) => data[uid] && data[uid].usertype === 'driver'
          );
          locations = await fetchLocationsForDriverIds(driverIds.slice(0, 50));
        }
      } catch (e) {
        console.warn('[fetchUsersOnce] locations fetch failed', e && e.message ? e.message : e);
      }

      dispatch({
        type: FETCH_ALL_USERS_STATIC_SUCCESS,
        payload: mapUsersRecordToArray(data, locations)
      });
    } catch (error) {
      dispatch({
        type: FETCH_ALL_USERS_STATIC_FAILED,
        payload: error && error.message ? error.message : "Failed to fetch users."
      });
    } finally {
      fetchUsersOnceInflight = null;
    }
  })();

  return fetchUsersOnceInflight;
};

export const getUsersPageKey = (usertype, fleetadminUid = null) =>
  fleetadminUid && usertype === 'driver'
    ? `driver:${fleetadminUid}`
    : fleetadminUid && usertype === 'customer'
      ? `customer:${fleetadminUid}`
      : usertype;

export const resetUsersPage = (pageKey) => (dispatch) => {
  dispatch({
    type: RESET_USERS_PAGE,
    payload: pageKey,
  });
};

export const fetchUsersPage = ({
  usertype,
  limit = ADMIN_USERS_PAGE_SIZE,
  startAfterKey = null,
  append = false,
  fleetadminUid = null,
  filterUsertype = null,
}) => async (dispatch, getState) => {
  const profile = getState && getState().auth && getState().auth.profile;
  if (!profile || !profile.usertype || !['admin', 'fleetadmin'].includes(profile.usertype)) {
    return;
  }

  const pageKey = getUsersPageKey(usertype, fleetadminUid);

  dispatch({
    type: FETCH_USERS_PAGE,
    payload: { pageKey, append },
  });

  try {
    const authUser = await waitForFirebaseAuth();
    if (!authUser) {
      dispatch({
        type: FETCH_USERS_PAGE_FAILED,
        payload: { pageKey, msg: 'Authentication required.' },
      });
      return;
    }

    const { database } = firebase;
    const pageSize = Math.min(Math.max(parseInt(limit, 10) || ADMIN_USERS_PAGE_SIZE, 1), 100);
    const useFleetadminIndex =
      fleetadminUid && (usertype === 'driver' || usertype === 'customer');
    const orderField = useFleetadminIndex ? 'fleetadmin' : 'usertype';
    const orderValue = useFleetadminIndex ? fleetadminUid : usertype;

    let usersQuery = query(
      ref(database, 'users'),
      orderByChild(orderField),
      equalTo(orderValue)
    );

    if (startAfterKey) {
      usersQuery = query(
        usersQuery,
        startAt(orderValue, startAfterKey),
        limitToFirst(pageSize + 1)
      );
    } else {
      usersQuery = query(usersQuery, limitToFirst(pageSize + 1));
    }

    const snap = await get(usersQuery);
    const val = snap.val() || {};
    let keys = Object.keys(val);

    if (startAfterKey && keys.length && keys[0] === startAfterKey) {
      keys = keys.slice(1);
    }

    const hasMore = keys.length > pageSize;
    const pageKeys = hasMore ? keys.slice(0, pageSize) : keys;
    let users = pageKeys
      .filter((uid) => val[uid])
      .map((uid) => ({
        ...val[uid],
        id: uid,
      }));

    if (filterUsertype) {
      users = users.filter((user) => user.usertype === filterUsertype);
    }

    const lastKey = pageKeys.length ? pageKeys[pageKeys.length - 1] : startAfterKey;

    dispatch({
      type: FETCH_USERS_PAGE_SUCCESS,
      payload: {
        pageKey,
        users,
        hasMore,
        lastKey,
        append,
      },
    });
  } catch (error) {
    dispatch({
      type: FETCH_USERS_PAGE_FAILED,
      payload: {
        pageKey,
        msg: error && error.message ? error.message : 'Failed to fetch users.',
      },
    });
  }
};

export const clearFetchDrivers = () => (dispatch) => {
  const {
    driversRef,
  } = firebase;
  off(driversRef);
  stopDriverLocationsPoll();
  driversFetchCache = null;
  locationsFetchCache = null;
}

/** Mapa admin web: solo GPS válido + en línea, sin exigir documentos aprobados. */
const mapDashboardDrivers = (data, locations) =>
  Object.keys(data || {})
    .filter((uid) => {
      const driver = data[uid];
      const loc = locations && locations[uid];
      return (
        driver &&
        loc &&
        loc.lat != null &&
        loc.lng != null &&
        loc.isOnline !== false
      );
    })
    .map((uid) => {
      const loc = locations[uid];
      const d = data[uid];
      return {
        id: uid,
        uid,
        location: loc,
        isOnline: loc.isOnline !== false,
        approved: d.approved !== false,
        driverActiveStatus: d.driverActiveStatus,
        carType: d.carType ? String(d.carType).trim() : null,
        vehicleNumber: d.vehicleNumber ? d.vehicleNumber : null,
        fleetadmin: d.fleetadmin ? d.fleetadmin : null,
        firstName: d.firstName,
        lastName: d.lastName,
        queue: d.queue,
        adActive: d.adActive ? d.adActive : false,
        adBalance: d.adBalance ? d.adBalance : 0,
      };
    });

const dispatchDriversUpdate = (dispatch, appType) => {
  if (!driversFetchCache) return;
  const settings = fetchDriversSettings || {};
  const mapper = appType === 'web' ? mapDashboardDrivers : mapOnlineDrivers;
  dispatch({
    type: FETCH_ALL_DRIVERS_SUCCESS,
    payload: mapper(driversFetchCache, locationsFetchCache, settings),
  });
};

const mapOnlineDrivers = (data, locations, settings) =>
  Object.keys(data || {})
    .filter((i) => data && data[i].approved == true && data[i].driverActiveStatus !== false && locations && locations[i]
      && isDispatchLocationFresh(locations[i])
      && ((data[i].licenseImage && settings.license_image_required) || !settings.license_image_required)
      && (((data[i].carApproved && settings.carType_required) || !settings.carType_required) || !settings.carType_required)
      && ((data[i].term && settings.term_required) || !settings.term_required))
    .map((i) => {
      const loc = locations && locations[i] ? locations[i] : null;
      return {
        id: i,
        location: loc,
        isOnline: data[i].driverActiveStatus !== false && loc && loc.isOnline !== false,
        approved: data[i].approved !== false,
        driverActiveStatus: data[i].driverActiveStatus,
        carType: data[i].carType ? String(data[i].carType).trim() : null,
        vehicleNumber: data[i].vehicleNumber ? data[i].vehicleNumber : null,
        fleetadmin: data[i].fleetadmin ? data[i].fleetadmin : null,
        firstName: data[i].firstName,
        lastName: data[i].lastName,
        queue: data[i].queue,
        adActive: data[i].adActive ? data[i].adActive : false,
        adBalance: data[i].adBalance ? data[i].adBalance : 0,
      };
    });

export const fetchDriversSnapshot = () => async () => {
  const { driversRef, settingsRef } = firebase;
  const loadOnce = async () => {
    await waitForFirebaseAuth(8000);
    const [settingsdata, driversSnap] = await Promise.all([
      get(settingsRef),
      get(driversRef),
    ]);
    const settings = settingsdata.val() || {};
    const data = driversSnap.val();
    if (!data) return [];
    const locations = await fetchLocationsForDriverIds(Object.keys(data));
    return mapOnlineDrivers(data, locations, settings);
  };

  try {
    let drivers = await loadOnce();
    if (!drivers.length) {
      await new Promise((r) => setTimeout(r, 400));
      drivers = await loadOnce();
    }
    if (!drivers.length) {
      await new Promise((r) => setTimeout(r, 800));
      drivers = await loadOnce();
    }
    return drivers;
  } catch (e) {
    console.warn('[fetchDriversSnapshot]', e && e.message ? e.message : e);
    return [];
  }
};

export const fetchDrivers = (appType) => async(dispatch) => {

  const {
    driversRef,
    settingsRef,
  } = firebase;

  // Este thunk se despacha sin .catch (MapScreen lo reintenta cada 5 s): un
  // rechazo aquí mostraba la caja "Operación no completada". Ante un fallo
  // transitorio seguimos con los ajustes en caché en vez de rechazar.
  try {
    const settingsdata = await get(settingsRef);
    fetchDriversSettings = settingsdata.val() || {};
  } catch (e) {
    console.warn('[fetchDrivers] settings fetch failed, using cached settings', e && e.message ? e.message : e);
    if (!fetchDriversSettings) fetchDriversSettings = {};
  }
  const useRealtime = !!fetchDriversSettings.realtime_drivers;

  const driversListenOpts =
    appType === 'web' ? null : (useRealtime && appType === 'app' ? null : { onlyOnce: true });

  try {
    off(driversRef);
    stopDriverLocationsPoll();
    driversFetchCache = null;
    locationsFetchCache = null;

    dispatch({
      type: FETCH_ALL_DRIVERS,
      payload: null
    });

    const syncDriverLocations = async () => {
      if (!driversFetchCache) return;
      locationsFetchCache = await refreshDriverLocationsCache();
      dispatchDriversUpdate(dispatch, appType);
    };

    onValue(driversRef, async snapshot => {
      // Firebase no captura rechazos de listeners async: sin este try/catch un
      // fallo de red aquí mostraba la caja "Operación no completada".
      try {
        driversFetchCache = snapshot.val();
        if (driversFetchCache) {
          await syncDriverLocations();
        } else {
          dispatch({
            type: FETCH_ALL_DRIVERS_FAILED,
            payload: "No users available."
          });
        }
      } catch (e) {
        console.warn('[fetchDrivers] listener sync failed', e && e.message ? e.message : e);
      }
    }, driversListenOpts);

    if (appType === 'web' || appType === 'app') {
      driverLocationsPollTimer = setInterval(() => {
        syncDriverLocations().catch((e) => {
          console.warn('[fetchDrivers] location poll failed', e && e.message ? e.message : e);
        });
      }, 10000);
    }
  } catch (e) {
    // Nunca rechazar: este thunk se despacha sin .catch y el rechazo llegaba
    // al manejador global como "Operación no completada".
    console.warn('[fetchDrivers] subscribe failed', e && e.message ? e.message : e);
    dispatch({
      type: FETCH_ALL_DRIVERS_FAILED,
      payload: "fetch_drivers_failed"
    });
  }
};

export const addUser = (userdata) => (dispatch) => {
  const {
    usersRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: userdata
  });

  delete userdata.tableData;

  push(usersRef, userdata).then((ref) => {
    dispatch({
      type: PATCH_USER_IN_LIST,
      payload: { ...userdata, id: ref.key },
    });
    dispatch({
      type: EDIT_USER_SUCCESS,
      payload: null
    });
  }).catch((error) => {
    dispatch({
      type: EDIT_USER_FAILED,
      payload: error
    });
  });
}

export const editUser = (id, user) => (dispatch) => {

  const {
    singleUserRef
  } = firebase;

  let editedUser = { ...user };
  delete editedUser.id;
  delete editedUser.tableData;

  dispatch({
    type: PATCH_USER_IN_LIST,
    payload: { ...user, id },
  });

  dispatch({
    type: EDIT_USER,
    payload: user
  });

  set(singleUserRef(id), editedUser);
}

export const updateUserCar = (id, data) => (dispatch) => {
  const {
    singleUserRef
  } = firebase;

  dispatch({
    type: EDIT_USER,
    payload: data  
  });
  update(singleUserRef(id),data);
}

export const updateLicenseImage = (uid, imageBlob, imageType) => async (dispatch) => {

  const {
    singleUserRef,
    driverDocsRef,
    driverDocsRefBack,
    verifyIdImageRef
  } = firebase;

  let profile = {};
  if(imageType === 'licenseImage'){
    await uploadBytesResumable(driverDocsRef(uid),imageBlob);
    let image = await getDownloadURL(driverDocsRef(uid));
    profile.licenseImage = image;
  }
  if(imageType === 'licenseImageBack'){
    await uploadBytesResumable(driverDocsRefBack(uid),imageBlob);
    let image1 = await getDownloadURL(driverDocsRefBack(uid));
    profile.licenseImageBack = image1;
  }
  if(imageType === 'verifyIdImage'){
    await uploadBytesResumable(verifyIdImageRef(uid),imageBlob);
    let image1 = await getDownloadURL(verifyIdImageRef(uid));
    profile.verifyIdImage = image1;
  }
  update(singleUserRef(uid),profile);
  dispatch({
    type: EDIT_USER,
    payload: uid
  });
};

export const deleteUser = (uid) => (dispatch) => {

  const {
    auth,
    walletHistoryRef,
    singleUserRef,
    userNotificationsRef,
    carsRef,
    carEditRef
  } = firebase;

  dispatch({
    type: DELETE_USER,
    payload: uid
  });

  if (auth.currentUser.uid === uid) {
    off(singleUserRef(uid));
    off(walletHistoryRef(uid));
    off(userNotificationsRef(uid));
  }

  onValue(singleUserRef(uid), userdata => {
    const profile = userdata.val();
    if(profile.usertype === 'driver'){
      onValue(carsRef(uid, profile.usertype), carssnapshot => {
        let cars = carssnapshot.val();
        if (cars) {
          const arr = Object.keys(cars);
          for(let i = 0; i < arr.length; i++){
            remove(carEditRef(arr[i]));
          }
        }
      });
    } 
    
    remove(singleUserRef(uid)).then(() => {
      if (auth.currentUser.uid === uid) {
        signOut(auth);
        dispatch({
          type: USER_DELETED,
          payload: null
        });
      } else {
        remove(singleUserRef(uid)).then(() => {
          dispatch({
            type: DELETE_USER_SUCCESS,
            payload: uid
          });
          dispatch({
            type: REMOVE_USER_FROM_LIST,
            payload: uid
          });
        }).catch((error) => {
          dispatch({
            type: DELETE_USER_FAILED,
            payload: error
          });
        });
      }
    });
  },{onlyOnce:true});
}

export const clearUsersError = () => (dispatch) => {
  dispatch({
    type: CLEAR_USERS_ERROR,
    payload: null
  });
};

export const approvePendingBankDetails = (id, user) => (dispatch) => {
  const { singleUserRef } = firebase;
  const pending = user && user.pendingBankDetails;
  if (!id || !pending) return;

  const updates = {
    bankName: pending.bankName,
    bankCode: pending.bankCode,
    bankAccount: pending.bankAccount,
    bankAccountType: pending.bankAccountType || 'Ahorros',
    bankDetailsStatus: 'approved',
    bankDetailsApprovedAt: Date.now(),
    pendingBankDetails: null,
  };

  update(singleUserRef(id), updates);
  dispatch({
    type: EDIT_USER,
    payload: { ...user, ...updates, id },
  });
};

export const rejectPendingBankDetails = (id, user) => (dispatch) => {
  const { singleUserRef } = firebase;
  if (!id) return;

  const updates = {
    bankDetailsStatus: user && user.bankAccount ? 'approved' : 'none',
    pendingBankDetails: null,
  };

  update(singleUserRef(id), updates);
  dispatch({
    type: EDIT_USER,
    payload: { ...user, ...updates, id },
  });
};
